/**
 * @file components/Navbar.jsx
 * @description Fixed top navigation bar.
 *
 * Uses the brand primary colour (#003d2d) as the background.
 * Shows the app name, navigation links filtered by the user's role,
 * and the user's display name with a logout button.
 *
 * Navigation items:
 *  - Dashboard      (all roles)
 *  - Tokens         (admin, operator)
 *  - Endpoints      (admin, operator)
 *  - Logs           (all roles)
 *  - Users          (admin only)
 */

import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { loginRequest } from '../auth/msalConfig';
import apiClient from '../api/client';

/**
 * Navigation item definition.
 * @typedef {Object} NavItem
 * @property {string}   to           - Route path.
 * @property {string}   label        - Display label.
 * @property {string}   icon         - Bootstrap Icons class name.
 * @property {string[]} [roles]      - Allowed roles; omit to show to all roles.
 */

/** All navigation items in display order. */
const NAV_ITEMS = [
  { to: '/',             label: 'Dashboard',    icon: 'bi-speedometer2'  },
  { to: '/tokens',       label: 'Tokens',       icon: 'bi-key',           roles: ['admin', 'operator'] },
  { to: '/proxy-routes', label: 'Proxy Routes', icon: 'bi-diagram-3',     roles: ['admin', 'operator'] },
  { to: '/endpoints',    label: 'Endpoints',    icon: 'bi-toggles',       roles: ['admin', 'operator'] },
  { to: '/logs',         label: 'Logs',         icon: 'bi-journal-text'  },
  { to: '/users',        label: 'Users',        icon: 'bi-people',        roles: ['admin'] },
  { to: '/settings',     label: 'Settings',     icon: 'bi-gear-fill',     roles: ['admin'] },
];

/**
 * Renders the top navigation bar.
 * Reads the active account from MSAL to display the user's name and role.
 */
export default function Navbar() {
  const { instance, accounts } = useMsal();
  const location               = useLocation();

  // Mobile hamburger toggle state — this app never loads Bootstrap's JS bundle
  // (only its SCSS), so the collapse/expand behaviour is handled in React
  // instead of relying on Bootstrap's data-bs-toggle attribute.
  const [mobileOpen, setMobileOpen] = useState(false);

  // The active account is set by AuthProvider after login
  const account = accounts[0];
  const displayName = account?.name || account?.username || '';

  // Fetch the local user record so we can filter nav items by role
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn:  async () => (await apiClient.get('/api/users/me')).data,
    enabled:  Boolean(account),
    staleTime: 10 * 60 * 1000,
  });

  // The login and setup pages have their own full-page layouts — no navbar there.
  // This must come AFTER every hook call above: an early return placed between
  // hooks changes how many hooks run depending on the route, which breaks
  // React's hook-order invariant ("Rendered more hooks than during the previous render").
  if (location.pathname === '/login' || location.pathname === '/setup') return null;

  const visibleNavItems = NAV_ITEMS.filter(
    (item) => !item.roles || item.roles.includes(currentUser?.role)
  );

  /**
   * Triggers an Entra ID logout, clearing both the local session and
   * the Entra ID session.
   */
  const handleLogout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  /**
   * Triggers an Entra ID login redirect.
   */
  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  return (
    <nav
      className="navbar navbar-expand-lg navbar-dark fixed-top"
      // No fixed height here: on mobile the expanded menu needs to grow taller
      // than --navbar-height, and a fixed height clipped that growth, letting
      // the menu overflow past the coloured box into transparent page area.
      style={{ backgroundColor: 'var(--color-primary)', minHeight: 'var(--navbar-height)' }}
    >
      <div className="container-fluid">
        {/* Brand / app title */}
        <NavLink className="navbar-brand fw-bold" to="/">
          <i className="bi bi-hdd-network me-2" aria-hidden="true" />
          NET-API
        </NavLink>

        {/* Hamburger toggle for mobile viewports */}
        <button
          className="navbar-toggler"
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-controls="main-nav"
          aria-expanded={mobileOpen}
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon" />
        </button>

        <div
          className={`collapse navbar-collapse${mobileOpen ? ' show' : ''}`}
          id="main-nav"
          // Explicit background so the expanded mobile panel is always opaque,
          // regardless of how the nav's own box height resolves.
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          {/* Main navigation links — rendered only when logged in */}
          {account && (
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              {visibleNavItems.map(({ to, label, icon }) => (
                <li className="nav-item" key={to}>
                  <NavLink
                    to={to}
                    className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                    end={to === '/'}
                    onClick={() => setMobileOpen(false)}
                  >
                    <i className={`bi ${icon} me-1`} aria-hidden="true" />
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>
          )}

          {/* User info + auth action (right-aligned) */}
          <div className="d-flex align-items-center gap-3 ms-auto">
            {account ? (
              <>
                <span className="text-light small" aria-label="Logged in as">
                  <i className="bi bi-person-circle me-1" aria-hidden="true" />
                  {displayName}
                </span>
                <button
                  className="btn btn-sm btn-outline-light"
                  onClick={() => { setMobileOpen(false); handleLogout(); }}
                  aria-label="Sign out"
                >
                  <i className="bi bi-box-arrow-right me-1" aria-hidden="true" />
                  Sign out
                </button>
              </>
            ) : (
              <button
                className="btn btn-sm"
                style={{ backgroundColor: 'var(--color-accent-1)', color: '#000' }}
                onClick={() => { setMobileOpen(false); handleLogin(); }}
                aria-label="Sign in with Microsoft"
              >
                <i className="bi bi-microsoft me-1" aria-hidden="true" />
                Sign in
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
